import { useLayoutEffect, useRef, useState } from "react";
import Tooltip from "@mui/material/Tooltip";

import type { CertificationItem } from "components/Screens/Marketplace/Customer/types";
import certificationIcon from "components/Screens/Marketplace/Customer/assets/common/certification-icon.svg";
import verifiedIcon from "components/Screens/Marketplace/Customer/assets/common/verified.svg";

import "components/Screens/Marketplace/Customer/ProfileSection/ProfileContent.scss";

const tooltipComponentsProps = {
  tooltip: {
    sx: {
      bgcolor: "#333333",
      color: "white",
      fontSize: "12px",
      maxWidth: "240px",
    },
  },
};

function CertificationTitle({ title }: { title: string }) {
  const titleRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return undefined;
    const check = () => setIsTruncated(el.scrollHeight - el.clientHeight > 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [title]);

  const titleEl = (
    <div ref={titleRef} className="certification-card__title">
      {title}
    </div>
  );

  if (!isTruncated) return titleEl;

  return (
    <Tooltip title={title} componentsProps={tooltipComponentsProps}>
      {titleEl}
    </Tooltip>
  );
}

interface CertificationsSectionProps {
  certifications: CertificationItem[];
}

function CertificationsSection({ certifications }: CertificationsSectionProps) {
  if (!certifications || certifications.length === 0) {
    return null;
  }

  return (
    <div id="certifications" className="profile-content__section">
      <div className="profile-content__title mb-075">Certifications</div>
      <div className="certifications-grid">
        {certifications.map((certification) => (
          <div key={`${certification.title}-${certification.issuedDate}`} className="certification-card">
            <div className="certification-card__icon">
              <img src={certificationIcon} alt="" />
            </div>
            <div className="certification-card__content">
              <div className="certification-card__title-row">
                <CertificationTitle title={certification.title} />
                {certification.isEdgeEdu && (
                  <Tooltip
                    title="Earned through Edge Edu—our internal program designed to upskill and certify talent."
                    componentsProps={tooltipComponentsProps}
                  >
                    <div className="certification-card__verified-icon">
                      <img src={verifiedIcon} alt="Edge Edu Certified" />
                    </div>
                  </Tooltip>
                )}
              </div>
              <div className="certification-card__date">{certification.issuedDate}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CertificationsSection;
